import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, X, Mail, Loader2, Shield, Users, Star, Info, Heart, Copy, Link, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useInviteStore } from '../store/inviteStore';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';


interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
}

interface InviteInput {
  name: string;
  email: string;
}

export const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, isLoading: externalLoading }) => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { remainingInvites, isLoading, error, sendInvite, getRemainingInvites, generateInvite } = useInviteStore();
  const [inviteInput, setInviteInput] = useState<InviteInput>({ name: '', email: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<{ url: string; code: string } | null>(null);
  const [showEmailFields, setShowEmailFields] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getRemainingInvites();
    }
  }, [isOpen, getRemainingInvites]);

  const handleInputChange = (field: keyof InviteInput, value: string) => {
    setInviteInput((prev) => ({ ...prev, [field]: value }));
  };

  const handleEmailInvite = async () => {
    try {
      if (!inviteInput.email.trim() || !inviteInput.name.trim()) {
        throw new Error('Please provide both name and email');
      }
      await sendInvite([
        {
          name: inviteInput.name,
          email: inviteInput.email,
          inviteMethod: 'email'
        }
      ]);
      setSuccessMessage(`Successfully sent invitation to ${inviteInput.email}`);
      setShowSuccess(true);
      setTimeout(() => {
        setInviteInput({ name: '', email: '' });
        setShowSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Invite error:', err);
      return;
    }
  };

  const handleGenerateLink = async () => {
    try {
      setIsGeneratingLink(true);
      // Generate invite code with default name if not provided and empty string for email if not provided
      const generatedInvite = await generateInvite({
  name: inviteInput.name || '',       // ← now blank instead of "Invited User"
  email: inviteInput.email || ''
});

      setGeneratedLink({
        url: `https://playclub.ai/invite?code=${generatedInvite.invite_code}`,
        code: generatedInvite.invite_code,
      });
    } catch (error) {
      console.error('Error generating link:', error);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyAll = async () => {
  if (!generatedLink) return;

  const message = `Hey, join me on playclub! \n\nDownload the app: ${generatedLink.url}\nUse my invite code ** ${generatedLink.code} ** when signing up!`;

  try {
    await navigator.clipboard.writeText(message);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  } catch (err) {
    console.error('Failed to copy:', err);
  }
};

  // Loading skeleton for the invite modal
  if (externalLoading) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
       <DialogContent className={cn(
  'w-[calc(100%-2rem)] max-w-[500px] border backdrop-blur-sm p-4 sm:p-6',
  isDarkMode 
    ? 'bg-dark-200/95 text-white border-dark-300/50' 
    : 'bg-white/95 text-gray-900 border-gray-200/50 shadow-xl'
)}>
  <DialogHeader>
    <DialogTitle>Loading Invitations…</DialogTitle>
  </DialogHeader>
  <div className="animate-pulse space-y-6">
    <div className="flex flex-col items-center space-y-4">
      <div className={cn(
        "w-16 h-16 rounded-full flex items-center justify-center",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}>
        <div className={cn(
          "w-8 h-8 rounded-full",
          isDarkMode ? "bg-dark-400" : "bg-gray-200"
        )}></div>
      </div>
      <div className={cn(
        "h-8 rounded-lg w-3/4",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}></div>
    </div>
    
    <div className={cn(
      "h-6 rounded-full w-40 mx-auto",
      isDarkMode ? "bg-dark-300" : "bg-gray-100"
    )}></div>

    <div className="space-y-4">
      <div className={cn(
        "h-12 rounded-lg",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}></div>
      <div className={cn(
        "h-12 rounded-lg",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}></div>
    </div>
    
    <div className={cn(
      "h-4 rounded-full w-full",
      isDarkMode ? "bg-dark-300" : "bg-gray-100"
    )}></div>
    
    <div className={cn(
      "h-12 rounded-lg",
      isDarkMode ? "bg-dark-300" : "bg-gray-100"
    )}></div>
    
    <div className="space-y-2">
      <div className={cn(
        "h-24 rounded-lg",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}></div>
      <div className={cn(
        "h-10 rounded-lg",
        isDarkMode ? "bg-dark-300" : "bg-gray-100"
      )}></div>
    </div>
    
    <div className={cn(
      "h-4 rounded-lg w-full mx-auto",
      isDarkMode ? "bg-dark-300" : "bg-gray-100"
    )}></div>
  </div>
</DialogContent>

      </Dialog>
    );
  }

  if (showSuccess) {
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className={cn(
          'sm:max-w-[425px] border',
          isDarkMode 
            ? 'bg-dark-200 text-white border-dark-300' 
            : 'bg-white text-gray-900 border-gray-200 shadow-xl'
        )}>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className={cn(
                "p-4 rounded-full",
                isDarkMode ? "bg-green-500/20" : "bg-green-100"
              )}>
                <Check className="h-8 w-8 text-green-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">Success!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center text-dark-800 text-lg">{successMessage}</div>
          </div>
          <Button 
            onClick={onClose} 
className="bg-gold-100 hover:bg-gold-200 text-dark-50 w-full mt-2"


            size="lg"
          >
            Done
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  if (remainingInvites === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn(
          'sm:max-w-[425px] border',
          isDarkMode 
            ? 'bg-dark-200 text-white border-dark-300' 
            : 'bg-white text-gray-900 border-gray-200 shadow-xl'
        )}>
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className={cn(
                "p-3 rounded-full",
                isDarkMode ? "bg-dark-300" : "bg-gray-100"
              )}>
                <X className={cn(
                  "h-6 w-6",
                  isDarkMode ? "text-gold-400" : "text-amber-500"
                )} />
              </div>
            </div>
            <DialogTitle className="text-center">No Invites Available</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end space-x-3">
            <Button onClick={onClose} className="bg-gold-400 hover:bg-gold-500 text-dark-50">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'w-[calc(100%-2rem)] max-w-[500px] border backdrop-blur-sm p-6',
          isDarkMode 
            ? 'bg-dark-200/95 text-white border-dark-300/50' 
            : 'bg-white/95 text-gray-900 border-gray-200/50 shadow-xl'
        )}
      >
        {/* Header with gold shield icon */}
              <DialogHeader className="mb-4">
  <DialogTitle className="text-center mb-4">Invite to playclub</DialogTitle>
</DialogHeader>

        <div className="flex flex-col items-center mb-6">
       <div className={cn(
  'p-4 rounded-full shadow-xl mb-4 border',
  isDarkMode 
    ? 'bg-dark-300 border-dark-400' 
    : 'bg-white border-gray-200'
)}>
  <img
    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png"
    alt="Playclub Logo"
    className="h-16 w-16 object-contain"
  />
</div>




          <div className="text-center mb-2">
            <span className={cn(
              'px-4 py-1.5 rounded-full inline-flex items-center border',
              isDarkMode 
                ? 'bg-dark-300/70 border-dark-400/30 text-gold-400' 
                : 'bbg-white border-gray-200 text-amber-600'
            )}>
              <Sparkles className={cn(
                "h-3.5 w-3.5 mr-1.5",
                isDarkMode ? "text-gold-400" : "text-amber-500"
              )} />
              You have {remainingInvites}  invitation{remainingInvites === 1 ? '' : 's'} remaining
           </span>
          </div>

          {error && (
            <div className={cn(
              'p-3 rounded-lg text-sm mt-2 w-full',
              isDarkMode ? 'bg-red-500/10 text-red-500' : 'bg-red-50 text-red-500 border border-red-100'
            )}>
              {error}
            </div>
          )}
        </div>

        
        {/* Main content area */}
        <LayoutGroup>
          <div className="space-y-6">
            {/* Generate Invite Code - Primary Action */}
            <Button
              type="button"
              onClick={handleGenerateLink}
              disabled={isGeneratingLink}
              className={cn(
                'w-full py-6 text-lg font-medium shadow-lg transition-all duration-300',
                isDarkMode 
                  ? 'bg-gold-400 hover:bg-gold-500 text-dark-50 hover:shadow-gold-400/20' 
                  : 'bg-gold-400 hover:bg-gold-500 text-dark-50 hover:shadow-amber-400/20'
              )}
            >
              {isGeneratingLink ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Generating...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <Link className="h-5 w-5" />
                  <span>Generate Invite Link</span>
                </div>
              )}
            </Button>
        
            {/* Generated Link Display */}
            <AnimatePresence>
              {generatedLink && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={cn(
                    'p-5 rounded-lg space-y-4 shadow-lg border',
                    isDarkMode 
                      ? 'bg-dark-300/70 border-gold-400/30' 
                      : 'bg-white border-gray-200'
                  )}
                >
                  <div className="space-y-2">
                    <div className={cn(
                      "text-sm font-medium",
                      isDarkMode ? "text-gold-400" : "text-amber-600"
                    )}>Invite link to share:</div>
                    <div className="relative">
                      <code className={cn(
                        "block w-full p-3 rounded-lg text-sm break-all pr-10",
                        isDarkMode ? "bg-dark-400/80 text-white" : "bg-gray-100 text-gray-800"
                      )}>
                        {generatedLink.url}
                      </code>
                     <button
  onClick={(e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(generatedLink.url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }}
  className={cn(
    "absolute top-1/2 right-3 -translate-y-1/2 p-1.5 rounded-md transition-colors",
    isDarkMode
      ? "bg-dark-500/50 hover:bg-dark-500 text-gold-400 hover:text-gold-300"
      : "bg-gray-200/50 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
  )}
  aria-label="Copy Invite Link"
  title="Copy Invite Link"
>
  {isCopied ? (
    <Check className="h-4 w-4" />
  ) : (
    <Copy className="h-4 w-4" />
  )}
</button>

                    </div>
                  </div>
                  
                  <details className={cn(
                    "mt-4 p-3 rounded-lg border",
                    isDarkMode ? "bg-dark-400/30 border-dark-400/50" : "bg-gray-100 border-gray-200"
                  )}>
                    <summary className={cn(
                      "text-sm font-medium cursor-pointer flex items-center",
                      isDarkMode ? "text-gold-400" : "text-amber-600"
                    )}>
                      <span className="mr-2">▼</span> Need the invite code only?
                    </summary>
                    <div className="mt-3 space-y-2">
                      <p className={cn(
                        "text-xs",
                        isDarkMode ? "text-dark-800" : "text-gray-500"
                      )}>Use this if they already have the app and just need to enter the invite code.</p>
                      <div className="relative">
                        <code className={cn(
                          "block w-full p-3 rounded-lg text-xl font-mono tracking-wider text-center pr-10",
                          isDarkMode ? "bg-dark-400/80 text-white" : "bg-gray-100 text-gray-800"
                        )}>
                          {generatedLink.code}
                        </code>
                        <button
  onClick={(e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(generatedLink.code);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }}
  className={cn(
    "absolute top-1/2 right-3 -translate-y-1/2 p-1.5 rounded-md transition-colors",
    isDarkMode
      ? "bg-dark-500/50 hover:bg-dark-500 text-gold-400 hover:text-gold-300"
      : "bg-gray-200/50 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
  )}
  aria-label="Copy Invite Code"
  title="Copy Invite Code"
>
  {isCopied ? (
    <Check className="h-4 w-4" />
  ) : (
    <Copy className="h-4 w-4" />
  )}
</button>

                      </div>
                    </div>
                  </details>
                  
                  <Button
  onClick={handleCopyAll}
  className={cn(
    "w-full",
    isDarkMode 
      ? "bg-gold-400 hover:bg-gold-500 text-dark-50" 
      : "bg-gold-400 hover:bg-gold-500 text-dark-50"
  )}
>
  <div className="flex items-center space-x-2">
    <Copy className="h-4 w-4" />
    <span>{isCopied ? 'Copied!' : 'Copy Invite Message to Share'}</span>
  </div>
</Button>

                </motion.div>
              )}
            </AnimatePresence>
          
            {/* Divider with "or" text */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className={cn(
                  "w-full border-t",
                  isDarkMode ? "border-dark-300/50" : "border-gray-200"
                )}></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className={cn(
                  "px-2",
                  isDarkMode ? "bg-dark-200 text-dark-800" : "bg-white text-gray-500"
                )}>or</span>
              </div>
            </div>
          
            {/* Email Invite Section - Secondary Option */}
            <div className="space-y-3">
              <Button
                onClick={() => setShowEmailFields(!showEmailFields)}
                className={cn(
                  "w-full",
                  isDarkMode 
                    ? "bg-dark-300 hover:bg-dark-400 text-white" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800"
                )}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>Invite via Email</span>
                </div>
              </Button>
              
              <AnimatePresence>
                {showEmailFields && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3 overflow-hidden"
                  >
                    <Input
                      value={inviteInput.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={cn(
                        'w-full transition-colors',
                        isDarkMode 
                          ? 'bg-dark-300/50 border-dark-400/30 text-white placeholder-dark-800/50 focus:border-gold-400/50' 
                          : 'bg-gray-100 border-gray-200 text-gray-800 placeholder-gray-500 focus:border-amber-400/50'
                      )}
                      placeholder="Invitee's Name"
                    />
                    
                    <div className="relative">
                      <Mail className={cn(
                        "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4",
                        isDarkMode ? "text-dark-800" : "text-gray-400"
                      )} />
                      <Input
                        type="email"
                        value={inviteInput.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={cn(
                          "pl-10 transition-colors",
                          isDarkMode 
                            ? "bg-dark-300/50 border-dark-400/30 text-white placeholder-dark-800/50" 
                            : "bg-gray-100 border-gray-200 text-gray-800 placeholder-gray-500"
                        )}
                        placeholder="Email address"
                      />
                    </div>
                    
                    <Button
                      onClick={handleEmailInvite}
                      disabled={!inviteInput.name || !inviteInput.email || isLoading}
                      className={cn(
                        "w-full",
                        isDarkMode 
                          ? "bg-gold-400 hover:bg-gold-500 text-dark-50" 
                          : "bg-gold-400 hover:bg-gold-500 text-dark-50"
                      )}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Sending...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <Mail className="h-4 w-4" />
                          <span>Send Email Invitation</span>
                        </div>
                      )}
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          
            {/* Footer note */}
            <div className="flex items-center justify-center gap-2 mt-2">
              <Info className={cn(
                "h-3.5 w-3.5",
                isDarkMode ? "text-dark-800" : "text-gray-500"
              )} />
              <p className={cn(
                "text-s text-center",
                isDarkMode ? "text-dark-800" : "text-gray-500"
              )}>
Invite people who'll make our community shine. ✨
              </p>
            </div>
          </div>
        </LayoutGroup>
      </DialogContent>
    </Dialog>
  );
};